import { supabase } from "@/integrations/supabase/client";

const SHOP_KEY = "pending_shopify_shop";
const SHOP_EMAIL_KEY = "pending_shopify_email";
const SHOP_NAME_KEY = "pending_shopify_name";
const SHOP_CURRENCY_KEY = "pending_shopify_currency";
const SESSION_KEY = "pending_shopify_session";

// Pending install sessions are generated by shopify-oauth-callback as UUIDs.
// Shopify's embedded launch also has a `session` query parameter, but that is
// an App Bridge session id and must never be sent to the install endpoints.
export function isPendingShopifyInstallToken(value: string | null | undefined): boolean {
  return Boolean(value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value));
}

export interface PendingShopifyContext {
  sessionToken: string | null;
  shop: string | null;
  shopEmail: string | null;
  shopName: string | null;
  shopCurrency: string | null;
}

export function getPendingShopifyContext(): PendingShopifyContext {
  if (typeof window === "undefined") {
    return {
      sessionToken: null,
      shop: null,
      shopEmail: null,
      shopName: null,
      shopCurrency: null,
    };
  }

  const rawSessionToken = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
  if (rawSessionToken && !isPendingShopifyInstallToken(rawSessionToken)) {
    // Clear data written by older builds that mistook Shopify's embedded
    // session id for our one-time OAuth install token.
    clearPendingShopifyContext();
  }

  return {
    sessionToken: isPendingShopifyInstallToken(rawSessionToken) ? rawSessionToken : null,
    shop: sessionStorage.getItem(SHOP_KEY),
    shopEmail: sessionStorage.getItem(SHOP_EMAIL_KEY),
    shopName: sessionStorage.getItem(SHOP_NAME_KEY),
    shopCurrency: sessionStorage.getItem(SHOP_CURRENCY_KEY),
  };
}

export function setPendingShopifyContext(context: {
  sessionToken?: string | null;
  shop?: string | null;
  shopEmail?: string | null;
  shopName?: string | null;
  shopCurrency?: string | null;
}): void {
  if (typeof window === "undefined") {
    return;
  }

  persistSessionValue(SESSION_KEY, context.sessionToken);
  if (context.sessionToken) {
    localStorage.setItem(SESSION_KEY, context.sessionToken);
  } else {
    localStorage.removeItem(SESSION_KEY);
  }
  persistSessionValue(SHOP_KEY, context.shop);
  persistSessionValue(SHOP_EMAIL_KEY, context.shopEmail);
  persistSessionValue(SHOP_NAME_KEY, context.shopName);
  persistSessionValue(SHOP_CURRENCY_KEY, context.shopCurrency);
}

export function clearPendingShopifyContext(): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SHOP_KEY);
  sessionStorage.removeItem(SHOP_EMAIL_KEY);
  sessionStorage.removeItem(SHOP_NAME_KEY);
  sessionStorage.removeItem(SHOP_CURRENCY_KEY);
}

export async function finalizePendingShopifyInstall(
  sessionToken?: string | null,
  options: { completeOnboarding?: boolean } = {},
) {
  const pendingContext = getPendingShopifyContext();
  const tokenToUse = sessionToken || pendingContext.sessionToken;
  if (!tokenToUse) {
    return null;
  }

  const { data, error } = await supabase.functions.invoke("finalize-shopify-install", {
    body: {
      sessionToken: tokenToUse,
      shopCurrency: pendingContext.shopCurrency,
      completeOnboarding: options.completeOnboarding === true,
    },
  });

  if (error) {
    throw error;
  }

  clearPendingShopifyContext();
  if (typeof window !== "undefined" && data?.merchantId) {
    localStorage.setItem("active_merchant_id", String(data.merchantId));
  }
  return data;
}

function persistSessionValue(key: string, value?: string | null) {
  if (!value) {
    sessionStorage.removeItem(key);
    return;
  }

  sessionStorage.setItem(key, value);
}
